<template>
  <v-dialog :value="value" persistent max-width="900px">
    <v-container fluid class="ma-0 pa-0">
      <v-card>
          <v-toolbar dense flat class="pl-4 pt-4">
            <v-icon class="mr-2 ml-0">
              {{ readOnly ? 'mdi-text-box-search-outline' : 'mdi-file-upload-outline' }}
            </v-icon>
            <v-toolbar-title class="title">
              {{ readOnly ? 'Contract Upload Details' : 'Upload Contract' }}
            </v-toolbar-title>
            <v-col cols="auto" class="ml-auto pr-1">
              <v-btn
                v-if="readOnly || ($auth.tenant !== 'jbg')"
               :loading="downloading" 
               :disabled="downloading" 
               :color="readOnly ? 'primary' : 'info'" 
               class="white--text" 
               @click="readOnly ? downloadContract() : downloadTemplate(is_complex)">
                <v-icon left>mdi-file-download-outline</v-icon>
                {{ readOnly ? 'Contract File' : 'Template' }}
              </v-btn>
            </v-col>
            <v-col v-if="!readOnly" cols="auto" class="pr-0">
              <v-btn 
                color="primary"
                :loading="loading" 
                :disabled="uploadDisabled"
                @click="uploadFile">
                <v-icon left>mdi-file-upload-outline</v-icon>
                {{ reupload ? "Reupload" : "Upload" }}
              </v-btn>
            </v-col>
            <v-col cols="auto" class="pr-0">
              <v-btn icon @click="$emit('closeUpload')">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-toolbar>
          <v-card-text class="py-4">
          <v-container class="pb-0" fluid>
            <p class="pt-0 mt-0 pl-2 ml-2" style="color: grey; font-size: 16px">
              {{ modalSubtitle }}
            </p>
              <v-form ref="form" lazy-validation v-model="valid">
                <v-row dense>
                  <v-col :cols="($auth.tenant === 'alliance-retail-group' ? undefined : 8)">
                    <template v-if="readOnly">
                      <v-text-field
                        v-model="selectedContract.file_name"
                        :inert="readOnly"
                        label="File Name"
                        dense
                        readonly
                        hide-details
                        prepend-icon="mdi-paperclip">
                      </v-text-field>
                    </template>
                    <template v-else>
                      <v-file-input
                        v-model="file"
                        :inert="readOnly"
                        outlined 
                        dense
                        hide-details="auto" 
                        truncate-length="100"
                        label="File*"
                        clearable
                        :rules="[v => !!v || 'Required field']"
                        accept=".xlsx"
                        @change="handleFile">
                      </v-file-input>
                    </template>
                  </v-col>
                  <v-col cols="6" class="pr-4" v-if="$auth.tenant === 'alliance-retail-group'">
                    <v-text-field
                      v-model="product_line"
                      :label="!is_complex ? 'Product Line*' : 'Promo Name'"
                      :rules="[v => !!v || 'Required field', validateText]"
                      dense
                      :outlined="!readOnly"
                      :readonly="readOnly"
                      :inert="readOnly"
                      hide-details="auto">
                    </v-text-field>
                  </v-col>
                  <v-col cols="3" class="pt-0" v-if="tenant === 'pricechopper'">
                    <v-checkbox
                      v-if="showMultiVendor"
                      v-model="payload.multi_vendor"
                      label="Private Label Contract"
                      color="primary"
                      class="mt-0 pt-0"
                      dense
                      :disabled="readOnly && !payload.multi_vendor"
                      :readonly="readOnly && payload.multi_vendor"
                      :inert="readOnly"
                      hide-details
                      :ripple="false"
                      @change="multiVendorChange">
                    </v-checkbox>
                    <v-checkbox
                      v-model="payload.non_allowance"
                      label="Non-Allowance Contract"
                      color="primary"
                      dense
                      :disabled="readOnly && !payload.non_allowance"
                      :readonly="readOnly && payload.non_allowance"
                      :inert="readOnly"
                      hide-details
                      :ripple="false">
                    </v-checkbox>
                  </v-col>
                </v-row>
                
                <v-container fluid class="px-4 pb-0">
                  <v-row dense class="mb-2" v-if="useVendorSelections">
                    <v-col cols="6">
                      <VendorSingleSelect
                        :disabled="payload.multi_vendor"
                        :readOnly="readOnly"
                        :value.sync="payload.vendor_party_id">
                      </VendorSingleSelect>
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="payload.subvendor_party_id"
                        :disabled="subvendorDisabled"
                        :items="subvendors"
                        item-text="name"
                        item-value="id"
                        item-color="action"
                        :label="tenant === 'alliance-retail-group' ? 'ARG Billing Vendor*' : 'Subvendor*'"
                        :loading="loadingSubvendors"
                        :rules="[v => payload.vendor_party_id ? (!!v || 'Required field') : true]"
                        validate-on-blur
                        :clearable="!readOnly"
                        :readonly="readOnly && Boolean(payload.subvendor_party_id)"
                        :inert="readOnly"
                        background-color="#fff"
                        :dense="!readOnly"
                        :outlined="!readOnly"
                        hide-details="auto">
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row dense class="pb-2">
                    <v-col v-if="useCategoryManager" cols="6">
                      <v-autocomplete
                        v-model="payload.category_manager_id"
                        :items="categoryManagers"
                        item-text="name"
                        item-value="id"
                        item-color="action"
                        label="Category Manager*"
                        :clearable="!readOnly"
                        :rules="[v => !!v || 'Required field']"
                        :disabled="readOnly && !payload.category_manager_id"
                        :readonly="readOnly && Boolean(payload.category_manager_id)"
                        :inert="readOnly"
                        validate-on-blur
                        background-color="#fff"
                        :dense="!readOnly"
                        :outlined="!readOnly"
                        hide-details="auto">
                      </v-autocomplete>
                    </v-col>
                    <v-col v-if="tenant === 'alliance-retail-group'" cols="6">
                      <ContractAdGroups 
                        :value.sync="payload.ad_group_party_ids"
                        :manuallyAdded.sync="manuallyAdded"
                        :readOnly="readOnly"
                        :reupload="reupload"
                        :adDatesSelected="adDatesSelected">
                      </ContractAdGroups>
                    </v-col>
                  </v-row>

                  <template v-if="tenant === 'alliance-retail-group'">
                    <v-divider class="my-2"></v-divider>

                      <v-row v-if="!is_complex">
                        <v-col v-if="readOnly || !payload.tpr_non_allowance" cols="2">
                          <v-text-field 
                            v-model="payload.lump_sum_allowance"
                            label="Ad Fee"
                            type="number"
                            min="0.00"
                            step=".01"
                            prefix="$"
                            :disabled="readOnly && !payload.lump_sum_allowance"
                            :readonly="readOnly && payload.lump_sum_allowance != null"
                            :outlined="!readOnly"
                            dense
                            hide-details="auto">
                          </v-text-field>
                        </v-col>
                        <v-col cols="2">
                          <v-text-field 
                            v-model="payload.tpr_fee"
                            label="TPR Fee"
                            type="number"
                            min="0.00"
                            step=".01"
                            prefix="$"
                            :disabled="readOnly && !payload.tpr_fee"
                            :readonly="readOnly && payload.tpr_fee != null"
                            :outlined="!readOnly"
                            dense
                            hide-details="auto">
                          </v-text-field>
                        </v-col>
                        <v-col 
                          v-for="obj in contractBoolsConfig"
                          :key="obj.value"
                          align-self="center"
                          cols="auto"
                          class="mx-0 pr-0">
                          <v-checkbox
                            v-model="payload[`${obj.value}`]"
                            :label="obj.label"
                            color="primary"
                            class="ma-auto"
                            dense
                            :readonly="readOnly && (payload[`${obj.value}`] === true)"
                            :disabled="(readOnly && !payload[`${obj.value}`]) || obj.disabled"
                            :inert="readOnly"
                            hide-details
                            @change="(e) => { return (obj.value === 'multi_vendor') ? multiVendorChange(e) : null }"
                            :ripple="false">
                          </v-checkbox>
                        </v-col>
                    </v-row>

                    <v-row>
                      <v-col v-if="!is_complex && (readOnly || !payload.tpr_non_allowance)" cols="4">
                        <v-autocomplete
                          v-model="payload.ad_billback_category_id"
                          :items="adBillbackPromoCategories"
                          item-text="name"
                          item-value="id"
                          item-color="action"
                          :label="`Ad Billback ${tenant === 'alliance-retail-group' ? 'Length' : 'Category'}`"
                          :clearable="!readOnly"
                          :disabled="(readOnly && !payload.ad_billback_category_id)"
                          :readonly="readOnly && Boolean(payload.ad_billback_category_id)"
                          :inert="readOnly"
                          background-color="#fff"
                          dense
                          :outlined="!readOnly"
                          hide-details="auto"
                          @click:clear="resetSpecialAdLength"
                          @change="resetSpecialAdLength">
                        </v-autocomplete>
                      </v-col>
                      <v-col v-if="!is_complex && (readOnly || !payload.tpr_non_allowance)" cols="4">
                        <v-autocomplete
                          v-model="payload.pcaf_promo_category_id"
                          :items="pcafBillbackPromoCategories"
                          item-text="name"
                          item-value="id"
                          item-color="action"
                          label="PCAF Category"
                          :clearable="!readOnly"
                          :disabled="(readOnly && !payload.pcaf_promo_category_id)"
                          :readonly="readOnly && Boolean(payload.pcaf_promo_category_id)"
                          :inert="readOnly"
                          background-color="#fff"
                          dense
                          :outlined="!readOnly"
                          hide-details="auto"
                          @click:clear="resetSpecialAdLength"
                          @change="resetSpecialAdLength">
                        </v-autocomplete>
                      </v-col>
                      <v-col v-if="is_complex" cols="4">
                        <v-autocomplete
                          v-model="payload.complex_promo_category_id "
                          :items="complexPromoCategories"
                          item-text="name"
                          item-value="id"
                          item-color="action"
                          label="Complex Promo Category"
                          :clearable="!readOnly"
                          :disabled="(readOnly && !payload.complex_promo_category_id )"
                          :readonly="readOnly && Boolean(payload.complex_promo_category_id )"
                          :rules="[v => !!v || 'Required field']"
                          :inert="readOnly"
                          background-color="#fff"
                          dense
                          :outlined="!readOnly"
                          hide-details="auto">
                        </v-autocomplete>
                      </v-col>
                      <v-col :cols="is_complex ? 'auto' : 4" align-self="center">
                        <v-checkbox
                          v-model="payload.special_ad_length"
                          label="Special Ad Length"
                          color="primary"
                          class="my-auto"
                          dense
                          :readonly="readOnly && payload.special_ad_length"
                          :disabled="isSpecialAdLengthDisabled"
                          :inert="readOnly"
                          hide-details
                          :ripple="false">
                        </v-checkbox>
                      </v-col>
                      <v-col
                        v-if="is_complex"
                        align-self="center"
                        cols="auto">
                        <v-checkbox
                          v-model="payload.dsd"
                          label="DSD"
                          color="primary"
                          class="ma-auto"
                          dense
                          :readonly="readOnly && payload.dsd"
                          :disabled="readOnly && !payload.dsd"
                          :inert="readOnly"
                          hide-details
                          :ripple="false">
                        </v-checkbox>
                      </v-col>
                      <v-col
                        v-if="(isAdmin || isARGAdmin) && is_complex"
                        align-self="center"
                        cols="auto">
                        <v-checkbox
                          v-model="payload.multi_vendor"
                          label="Multi Vendor"
                          color="primary"
                          class="ma-auto"
                          dense
                          :readonly="readOnly && payload.multi_vendor"
                          :disabled="readOnly && !payload.multi_vendor"
                          :inert="readOnly"
                          hide-details
                          @change="multiVendorChange"
                          :ripple="false">
                        </v-checkbox>
                      </v-col>
                    </v-row>

                    <v-row v-if="readOnly || !payload.tpr_non_allowance">
                      <v-col cols="8" align-self="center">
                        <v-text-field 
                          v-model="payload.digital_coupon_note"
                          :label="`${tenant === 'alliance-retail-group' ? 'ARG ' : ''} Digital Coupon Value`"
                          :disabled="!payload.digital_coupon"
                          :readonly="readOnly && payload.digital_coupon"
                          :inert="readOnly"
                          :rules="digitalCouponRules"
                          :outlined="!readOnly"
                          dense
                          hide-details="auto">
                        </v-text-field>
                      </v-col>
                      <v-col cols="auto" align-self="center">
                        <v-checkbox
                          v-model="payload.digital_coupon"
                          :label="`${tenant === 'alliance-retail-group' ? 'ARG ' : ''} Digital Coupon`"
                          color="primary"
                          class="my-auto"
                          :disabled="(readOnly && !payload.digital_coupon)"
                          :readonly="readOnly && payload.digital_coupon"
                          :inert="readOnly"
                          dense
                          hide-details
                          :ripple="false">
                        </v-checkbox>
                      </v-col>
                    </v-row>

                    <v-divider class="my-2"></v-divider>

                    <v-row dense class="pt-2">
                      <v-col 
                        v-for="config in contractDatesConfig"
                        :cols="config.cols || 4"
                        :key="config.value"
                        :class="readOnly ? undefined : 'py-2'">
                        <ContractDates
                          :ref="`${config.value}_dates`"
                          :value.sync="payload[`${config.value}`]"
                          :readOnly="readOnly"
                          :config="config"
                          :specialAdLength="payload.special_ad_length"
                          :ad_billback_category_id="payload.ad_billback_category_id"
                          :pcaf_promo_category_id="payload.pcaf_promo_category_id"
                          :isComplex="is_complex"
                          hide-details="auto"
                          :error="showDateError">
                        </ContractDates>
                      </v-col>
                    </v-row>
                  </template>
                </v-container>
              </v-form>
          </v-container>
        </v-card-text>
      </v-card>
    </v-container>
    <UploadDialog v-if="dialog" v-model="dialog" :file="file" @cancel="closeUploadDialog"
      @confirmUpload="confirmUpload">
    </UploadDialog>
    <ConfirmDatesDialog
      v-if="confirmDatesDialog"
      v-model="confirmDatesDialog"
      :pastDates="pastDates"
      :tprDatesLessThanThreeWeeks="tprDatesLessThanThreeWeeks"
      :tprStartDate="payload['tpr'].start_dt"
      :tprEndDate="payload['tpr'].end_dt"
      @cancel="closeConfirmDatesDialog"
      @openConfirm="confirmDates">
    </ConfirmDatesDialog>
  </v-dialog>
</template>
<script>
// api
import ContractUpload from '@/axios/contract-upload-endpoint.js'
import PartyRel from '@/axios/party-relationship-endpoint.js'
// components

import VendorSingleSelect from '@/components/VendorSingleSelect'
import ContractDates from '@/components/contracts/ContractDates.vue'
import ContractAdGroups from '@/components/contracts/ContractAdGroups.vue'
// mixins
import { displayAlert } from '@/mixins/alert'
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
import { formValidation } from '@/mixins/form-validation'
// third party helpers
import { addMinutes } from 'date-fns'

export default {
  name: 'ContractUploadModal',
  data() {
    return {
      vendors: [],
      subvendors: [],
      manuallyAdded: [],
      loadingSubvendors: false,
      file: null,
      newFile: false,
      valid: true,
      loading: false,
      downloading: false,
      dialog: false,
      confirmDatesDialog: false,
      showDateError: false,
      product_line: null,
      pastDates: [],
      payload: {
        vendor_party_id: null,
        subvendor_party_id: null,
        category_manager_id: null,
        ad_group_party_ids: [],
        multi_vendor: false,
        non_allowance: false,
        tpr_non_allowance: false,
        dsd: false,
        perishable: false,
        ad_billback_category_id: null,
        pcaf_promo_category_id: null,
        complex_promo_category_id: null,
        override_store_offset: false,
        override_store_length: false,
        lump_sum_allowance: null,
        tpr_fee: null,
        special_ad_length: false,
        tpr: { start_dt: null, end_dt:  null },
        edlc: { start_dt: null, end_dt:  null },
        amap: { start_dt: null, end_dt:  null },
        eba: { start_dt: null, end_dt:  null },
        dsd_off_invoice: { start_dt: null, end_dt: null },
        ad_1: { start_dt: null, end_dt:  null, offer_start_date: null, offer_end_date: null },
        ad_2: { start_dt: null, end_dt:  null, offer_start_date: null, offer_end_date: null },
        ad_3: { start_dt: null, end_dt:  null, offer_start_date: null, offer_end_date: null },
        digital_coupon: false,
        digital_coupon_note: null,
      }
    }
  },
// ad_1 , ad_2  and ad_3  date inputs will be used for both PCAF and ad billback promos created by the upload, so we need to implement logic to ensure that the selections are "compatible" from a date rules/restrictions perspective:
//    Example: if "Ad Billback - 1 Week Ad" is chosen, the only valid PCAF category would be "PCAF - 1 Week Ad", and vice versa.
//    Note: it's still valid for the user to just select a PCAF category, so we'll need to update the existing date rules to account for this.
  mixins: [displayAlert, userAccess, utils, formValidation],
  components: { 
    UploadDialog: () => import('@/components/contracts/UploadDialog.vue'),
    ConfirmDatesDialog: () => import('@/components/contracts/ConfirmDatesDialog.vue'),
    VendorSingleSelect, 
    ContractDates, 
    ContractAdGroups 
  },
  props: {
    value: Boolean,
    reupload: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    uploadId: {
      type: String,
      default: ""
    },
    is_complex: {
      type: Boolean,
      default: false
    },
    selectedContract: Object
  },
  watch: {
    'payload.vendor_party_id': {
      async handler(newValue, oldValue) {
        if (newValue) {
          if (oldValue) {
            this.payload.subvendor_party_id = null
          }
          await this.updateSubvendors()
        }
      }
    },
    'payload.digital_coupon': {
      handler(newValue) {
        if (!this.readOnly && newValue === false) {
          this.payload.digital_coupon_note = null
        }
      }
    },
    'payload.tpr_non_allowance': {
      handler(newValue) {
        if (!this.readOnly && newValue === true) {
          this.clearNonTPRValues()
        }
      }
    },
    'payload.pcaf_promo_category_id': {
      handler(newValue) {
        if (!this.readOnly && newValue) {
          this.payload.perishable = true
        } else if (!newValue) {
          this.payload.perishable = false
        }
      }
    },
    noPromoDatesSelected: {
      handler(newValue) {
        if (newValue === false) {
          this.showDateError = false
        }
      }
    }
  },
  async created () {
    if ((this.reupload || this.readOnly) && this.selectedContract?.payload) {
      const payload = JSON.parse(JSON.stringify(this.selectedContract.payload))
      this.payload = payload
      this.product_line = this.selectedContract.product_line
    }
    if (this.promo_categories.length === 0) {
      await this.$store.dispatch('getPromoCategories')
    }
  },
  computed: {
    modalSubtitle () {
      if (this.readOnly) {
        return 'Values entered for this contract'
      }
      return this.reupload ? "Reupload the contract file." : "Create promotions via contract file."
    },
    subvendorDisabled () {
      return !this.payload.vendor_party_id || this.payload.multi_vendor
    },
    useVendorSelections () {
      return ['alliance-retail-group', 'pricechopper'].includes(this.tenant) && !this.payload.multi_vendor
    },
    useCategoryManager () {
      return ['alliance-retail-group', 'pricechopper'].includes(this.tenant)
    },
    totalPromoExcludedAdGroups() {
      return this.$store.getters.totalPromoExcludedAdGroups
    },
    adPromoOnlyExcludedAdGroups() {
      return this.$store.getters.adPromoOnlyExcludedAdGroups
    },
    categoryManagers () {
      return this.$store.getters.categoryManagers
    },
    showMultiVendor () {
      return this.tenant === 'pricechopper' && (this.isAdmin || this.userPermissions.includes('contract:multi_vendor'))
    },
    billing_modes () {
      return this.$store.getters.billing_modes
    },
    dateExcludedCategories () {
      if (this.$auth.tenant === 'alliance-retail-group') {
        const categories = this.userPromoCategories.filter(cat => {
          const normalizedName = cat.name?.toLowerCase()?.replaceAll('-', ' ')
          return (normalizedName.includes('day ad') || normalizedName.includes('non allowance')) && cat.billback_promotion === true
        })
        return categories.flatMap(cat => cat?.name || [])
      }
      return []
    },
    adBillbackPromoCategories () {
      if (this.$auth.tenant === 'alliance-retail-group') {
        const categories = this.userPromoCategories.filter(cat => {
          const normalizedCatName = cat.name.toLowerCase()?.replaceAll('-', ' ')
          const isAdBillback = cat.billback_promotion === true && normalizedCatName.includes('ad') && this.billing_modes.some(bm => cat.valid_billing_modes.includes(bm?.id) && bm.constant === 'AD')
          if (isAdBillback && this.payload.pcaf_promo_category_id) {
            const normalizedPcafName = this.selectedPcafCategoryName.toLowerCase()?.replaceAll('-', ' ')
            const restriction = ['1 2 day', '1 week', '2 week', 'non standard'].find(restriction => normalizedPcafName.includes(restriction))
            return restriction && normalizedCatName.includes(restriction)
          }
          return isAdBillback
        })
        return this.sortPromoCategories(categories)
      }
      return []
    },
    pcafBillbackPromoCategories () {
      if (this.$auth.tenant === 'alliance-retail-group') {
        const categories = this.userPromoCategories.filter(cat => {
          const normalizedCatName = cat.name.toLowerCase()?.replaceAll('-', ' ')
          if (this.payload.ad_billback_category_id) {
            const normalizedAdBillbackName = this.selectedAdBillbackCategoryName.toLowerCase()?.replaceAll('-', ' ')
            const restriction = ['1 2 day', '1 week', '2 week', 'non standard'].find(restriction => normalizedAdBillbackName.includes(restriction))
            return restriction && normalizedCatName.startsWith('pcaf') && normalizedCatName.includes(restriction)
          }
          return normalizedCatName.startsWith('pcaf')
        })
        return this.sortPromoCategories(categories)
      }
      return []
    },
    complexPromoCategories () {
      return this.userPromoCategories.filter(pc => ['BUY_X_GET_X', 'BUY_X_SAVE_$Y'].includes(pc?.promo_type?.constant))
    },
    selectedAdBillbackCategoryName() {
      if (this.payload.ad_billback_category_id) {
        const category = this.userPromoCategories.find(c => c?.id === this.payload.ad_billback_category_id)
        return category?.name
      }
      return null
    },
    selectedPcafCategoryName() {
      if (this.payload.pcaf_promo_category_id) {
        const category = this.userPromoCategories.find(c => c?.id === this.payload.pcaf_promo_category_id)
        return category?.name
      }
      return null
    },
    isSpecialAdLengthDisabled() {
      if (this.readOnly && !this.payload.special_ad_length) return true
      if (this.is_complex) return !this.payload.complex_promo_category_id
      return !this.payload.ad_billback_category_id || this.dateExcludedCategories.includes(this.selectedAdBillbackCategoryName)
    },
    adNonAllowanceSelected() {
      if (this.$auth.tenant === 'alliance-retail-group') {
        const normalized = this.selectedAdBillbackCategoryName?.toLowerCase()?.replaceAll('-', ' ')
        return normalized?.includes('non allowance')
      }
      return false
    },
    nonAllowanceCategoryId() {
      if (this.$auth.tenant === 'alliance-retail-group') {
        const category = this.adBillbackPromoCategories.find(c => { 
          const normalized = c?.name.toLowerCase()?.replaceAll('-', ' ')
          return normalized?.includes('non allowance')
        })
        return category?.id
      }
      return null
    },
    tprDatesLessThanThreeWeeks() {
      if (this.payloadDatesSelected('tpr')) {
        const start = new Date(this.payload['tpr'].start_dt)
        const end = new Date(this.payload['tpr'].end_dt)
        const promoLength = ((this.getTimezoneOffset(end) - this.getTimezoneOffset(start)) / (1000 * 3600 * 24)) + 1
        return promoLength < 21
      }
      return false
    },
    uploadDisabled () {
      return this.loading || !this.file 
          || (this.useVendorSelections && (!this.payload.vendor_party_id || !this.payload.subvendor_party_id))
          || (this.useCategoryManager && !this.payload.category_manager_id)
          || (this.is_complex && !this.payload.complex_promo_category_id)
          || (this.tenant === 'alliance-retail-group' && (this.payload.ad_group_party_ids.length === 0) || this.noPromoDatesSelected)
    },
    contractDatesConfig() {
      if (this.$auth.tenant !== 'alliance-retail-group') return []
      return [
        {
          label: 'AMAP Start/End',
          value: 'amap',
          billing_mode: 'AMAP',
          off_invoice: true,
          disabled: (this.readOnly && !this.payloadDatesSelected('amap')),
          show: !this.is_complex
        },
        {
          label: 'EBA Start/End',
          value: 'eba',
          billing_mode: 'EBA',
          off_invoice: true,
          disabled: (this.readOnly && !this.payloadDatesSelected('eba')),
          show: !this.is_complex
        },
        {
          label: 'DSD OI Start/End',
          value: 'dsd_off_invoice',
          billing_mode: 'OI',
          off_invoice: true,
          disabled: (this.readOnly && !this.payloadDatesSelected('dsd_off_invoice')) || !this.payload.dsd,
          show: !this.is_complex && (this.readOnly || !this.payload.tpr_non_allowance)
        },
        {
          label: 'EDLC Start/End',
          value: 'edlc',
          billing_mode: 'EDLC',
          disabled: (this.readOnly && !this.payloadDatesSelected('edlc')),
          show: !this.is_complex && (this.readOnly || !this.payload.tpr_non_allowance)
        },
        {
          label: 'TPR Start/End',
          value: 'tpr', billing_mode:'TPR',
          disabled: (this.readOnly && !this.payloadDatesSelected('tpr')),
          show: !this.is_complex
        },
        {
          label: 'Ad 1 Start/End', value: 'ad_1', billing_mode: 'AD', cols: 4,
          disabled: (this.readOnly && !this.payloadDatesSelected('ad_1')),
          show: (this.readOnly || !this.payload.tpr_non_allowance)
        },
        {
          label: 'Ad 2 Start/End', value: 'ad_2', billing_mode: 'AD', cols: 4,
          disabled: (this.readOnly ? !this.payloadDatesSelected('ad_2') : !this.payloadDatesSelected('ad_1')),
          min: this.payload.ad_1?.end_dt,
          show: !this.is_complex && (this.readOnly || !this.payload.tpr_non_allowance)
        },
        {
          label: 'Ad 3 Start/End', value: 'ad_3', billing_mode: 'AD', cols: 4,
          disabled: this.readOnly ? !this.payloadDatesSelected('ad_3') : !this.payloadDatesSelected('ad_2'),
          min: this.payload.ad_2?.end_dt,
          show: !this.is_complex && (this.readOnly || !this.payload.tpr_non_allowance)
        },
      ].filter(date => date.show)
    },
    contractBoolsConfig () {
      if (this.$auth.tenant !== 'alliance-retail-group') return []
      return [
        { label: 'TPR Non Allowance', value: 'tpr_non_allowance', disabled: false, show: !this.is_complex && !this.limitAccessByRelatedVendors },
        { label: 'Perishable', value: 'perishable', disabled: this.payload.dsd === true || Boolean(this.payload.pcaf_promo_category_id), show: !this.is_complex },
        { label: 'DSD', value: 'dsd', disabled: [this.payload.perishable, this.payload.multi_vendor].some(v => !!v), show: !this.payload.tpr_non_allowance },
        { label: 'Private Label', value: 'multi_vendor', disabled: this.payload.dsd || (!this.payload.tpr_non_allowance && !this.adNonAllowanceSelected), show: (!this.is_complex && !this.limitAccessByRelatedVendors) },
      ].filter(bool => bool.show)
    },
    contractDateLabels() {
      return this.contractDatesConfig.flatMap(date => date.value)
    },
    incompleteDates () {
      const incompleteDateFields = []
      this.contractDateLabels.forEach(df => {
        if ((this.payload[df].start_dt && !this.payload[df].end_dt) || (!this.payload[df].start_dt && this.payload[df].end_dt)) {
          const label = this.contractDatesConfig.find(cdc => cdc.value === df).label
          incompleteDateFields.push(label)
        }
      })
      return incompleteDateFields
    },
    noPromoDatesSelected () {
      return (this.$auth.tenant === 'alliance-retail-group') && !this.adDatesSelected && !this.tprDatesSelected && !this.payloadDatesSelected('dsd_off_invoice')
    },
    adDatesSelected() {
      return (this.$auth.tenant === 'alliance-retail-group') && this.contractDatesConfig.some(config => {
        return (config.value.includes('ad')) && this.payloadDatesSelected(config.value)
      })
    },
    tprDatesSelected() {
      return (this.$auth.tenant === 'alliance-retail-group') && this.contractDatesConfig.some(config => {
        return (['tpr', 'edlc'].includes(config.value)) && this.payloadDatesSelected(config.value)
      })
    },
    digitalCouponRules() {
      if (this.payload.digital_coupon) {
        return [v => !!v || 'Digital Coupon Value is required', this.validateText];
      }
      return []
    },
    // TO BE REIMPLEMENTED AT A FUTURE DATE
    // checkTprEdlcOverlap() {
    //   const tprDates = this.payload.tpr
    //   const edlcDates = this.payload.edlc
      
    //   if (tprDates?.start_dt && tprDates?.end_dt && edlcDates?.start_dt && edlcDates?.end_dt) {
    //     const tprStart = this.getExactDay(tprDates.start_dt)
    //     const tprEnd = this.getExactDay(tprDates.end_dt)
    //     const edlcStart = this.getExactDay(edlcDates.start_dt)
    //     const edlcEnd = this.getExactDay(edlcDates.end_dt)

    //     return (tprStart <= edlcEnd && tprEnd >= edlcStart)
    //   }
    //   return false
    // },
  },
  methods: {
    handleFile(file) {
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.file.upload_file = e.target.result
        }
        reader.readAsArrayBuffer(file)
      }
    },
    getExactDay(value) {
      let date = new Date(value)
      date = addMinutes(date, date.getTimezoneOffset())
      return date
    },
    reset() {
      this.file = null
    },
    confirmDates() {
      this.confirmDatesDialog = false
      this.validateFields()
    },
    getTimezoneOffset (value) {
      let date = new Date(value)
      return addMinutes(date, date.getTimezoneOffset())
    },
    getPastDates() {
      const today = new Date()
      const pastDates = [
        { label: 'Ad 1 Start Date', date: this.payload.ad_1.start_dt },
        { label: 'Ad 1 End Date', date: this.payload.ad_1.end_dt }, 
        { label: 'Ad 2 Start Date', date: this.payload.ad_2.start_dt },
        { label: 'Ad 2 End Date', date: this.payload.ad_2.end_dt }, 
        { label: 'Ad 3 Start Date', date: this.payload.ad_3.start_dt }, 
        { label: 'Ad 3 End Date', date:this.payload.ad_3.end_dt }, 
        { label: 'TPR Start Date', date: this.payload.tpr.start_dt },
        { label: 'TPR End Date', date: this.payload.tpr.end_dt },
        { label: 'EDLC Start Date', date: this.payload.edlc.start_dt }, 
        { label: 'EDLC End Date', date: this.payload.edlc.end_dt }
      ].filter(pd => { return pd.date !== null && this.getExactDay(pd.date) < this.getExactDay(today) })
      return pastDates
    },
    getMinStartDate (dateRanges) {
      let min = null
      dateRanges.forEach(ad => {
        if (this.payload[ad]?.start_dt) {
          const curr = new Date(this.payload[ad]?.start_dt)
          if (!min || curr < min) {
            min = curr
          }
        }
      })
      return min
    },
    getMaxEndDate (dateRanges) {
      let max = null
      dateRanges.forEach(ad => {
        if (this.payload[ad]?.end_dt) {
          const curr = new Date(this.payload[ad]?.end_dt)
          if (!max || curr > max) {
            max = curr
          }
        }
      })
      return max
    },
    validateDateRanges (min, max, dateRangeValues, type) {
      let valid = true
      dateRangeValues.forEach(rangeValue => {
        if (this.payload[rangeValue]?.start_dt && this.payload[rangeValue]?.end_dt) {
          const start = new Date(this.payload[rangeValue]?.start_dt)
          const end = new Date(this.payload[rangeValue]?.end_dt)
          if (['tpr', 'edlc'].includes(rangeValue)) {
            start.setDate(start.getDate() - 1)
            end.setDate(end.getDate() + 1)
          }
          const validRange = (start <= min) && (end >= max)
          if (!validRange) {
            this.emitAlert(true, 'warning', `Please ensure ${this.contractDatesConfig.find(config => config.value === rangeValue).label} date overlaps the entire ${type} date range (within 1 day of the ${type} starting and ending dates).`)
            valid = false
          }
        }
      })
      return valid
    },
    checkLateSubmission(value, maxNumberOfDays) {
      let late = false
      const currentDate = new Date()
      if (this.payload[value]?.start_dt) {
        const startDate = new Date(this.payload[value]?.start_dt)
        const diff = Math.abs((startDate - currentDate) / (1000 * 3600 * 24))
        if (diff < maxNumberOfDays) late = true
      }
      return late
    },
    checkLateSubmissions() {
      let warning = ''
      const promoValues = ['tpr', 'edlc']
      let latePromoSubmissionNames = ''
      for (const pv of promoValues) {
        const late = this.checkLateSubmission(pv, 28)
        if (late) {
          if (!latePromoSubmissionNames) latePromoSubmissionNames += `${pv.toUpperCase()}`
          else latePromoSubmissionNames += ` / ${pv.toUpperCase()}`
        }
      }
      if (latePromoSubmissionNames) warning += `${latePromoSubmissionNames} start date entered is less than ${28 / 7} weeks from current date. `
      
      let lateAdNames = ''
      const maxAdDays = this.payload.perishable ? 28 : 56
      const adValues = [
        { value: 'ad_1', name: 'Ad 1' },
        { value: 'ad_2', name: 'Ad 2' },
        { value: 'ad_3', name: 'Ad 3' }
      ]
      for (const av of adValues) {
        const late = this.checkLateSubmission(av.value, maxAdDays)
        if (late) {
          if (!lateAdNames) lateAdNames += `${av.name}`
          else lateAdNames += ` / ${av.name}`
        }
      }
      if (lateAdNames) warning += `${lateAdNames} start date(s) entered are less than ${maxAdDays / 7} weeks from current date. `

      if (warning) {
        warning += 'Please reach out to ARG Category Manager for review and approval after uploading.'
        this.emitAlert(true, 'warning', warning)
      }
    },
    uploadFile() {
      if (this.tenant !== 'alliance-retail-group') {
        return this.dialog = true
      }
      if (this.incompleteDates.length > 0) {
        const incompleteDateFields = this.incompleteDates.join(', ')
        return this.emitAlert(true, 'warning', `Incomplete date range: ${incompleteDateFields}`)
      }
      if (this.noPromoDatesSelected) {
        this.showDateError = true
        return this.emitAlert(true, 'warning', 'No promo start/end dates selected')
      }
      // TO BE REIMPLEMENTED AT A FUTURE DATE
      // if (this.checkTprEdlcOverlap) {
      //   return this.emitAlert(true, 'warning', 'TPR and EDLC date ranges cannot overlap')
      // }
      if (this.payload.ad_billback_category_id && !this.adDatesSelected) {
        return this.emitAlert(true, 'warning', 'Ad billback category selected but no ad start/end dates entered')
      }
      if (this.payload.pcaf_promo_category_id && !this.adDatesSelected) {
        return this.emitAlert(true, 'warning', 'PCAF promo category selected but no ad start/end dates entered')
      }
      if (this.payload.digital_coupon && !this.adDatesSelected) {
        return this.emitAlert(true, 'warning', 'Digital coupon checked but no ad start/end dates entered')
      }
      if (((this.payload.lump_sum_allowance || 0) > 0) && !this.adDatesSelected) {
        return this.emitAlert(true, 'warning', 'Ad fee detected but no ad start/end dates entered')
      }
      if (((this.payload.tpr_fee || 0) > 0) && !this.tprDatesSelected) {
        return this.emitAlert(true, 'warning', 'TPR fee detected but no TPR or EDLC start/end dates entered')
      }
      if (this.payload.multi_vendor && !(this.payload.tpr_non_allowance || this.adNonAllowanceSelected || this.is_complex)) {
        return this.emitAlert(true, 'warning', 'Private Label checked but neither Ad Non Allowance nor TPR Non Allowance selected')
      }
      if (this.$auth.tenant === 'alliance-retail-group') {
        this.pastDates = this.getPastDates()
        if (this.pastDates.length > 0 || this.tprDatesLessThanThreeWeeks) {
          return this.confirmDatesDialog = true
        }
      }
      this.validateFields()
    },
    validateFields() {
      let validDates = false
        for (const config of this.contractDatesConfig) {
          const field = this.$refs[`${config.value}_dates`][0]
          if (this.payload[config.value].start_dt && this.payload[config.value].end_dt) {
            validDates = field.validate()
          }
        }
        if (validDates) {
          const minAdStart = this.getMinStartDate(['ad_1', 'ad_2', 'ad_3'])
          const maxAdEnd = this.getMaxEndDate(['ad_1', 'ad_2', 'ad_3'])
          const minAllowanceStart = this.getMinStartDate(['ad_1', 'ad_2', 'ad_3', 'tpr', 'edlc'])
          const maxAllowanceEnd = this.getMaxEndDate(['ad_1', 'ad_2', 'ad_3', 'tpr', 'edlc'])

          let validAdDependents = true
          let validAllowanceDependents = true
          if (this.adDatesSelected) {
            validAdDependents = this.validateDateRanges(minAdStart, maxAdEnd, ['tpr', 'edlc'], 'ad')
          }
          if (this.adDatesSelected || this.tprDatesSelected) {
            if (this.tprDatesSelected) {
              minAllowanceStart.setDate(minAllowanceStart.getDate() + 1)
              maxAllowanceEnd.setDate(maxAllowanceEnd.getDate() - 1)
            }
            validAllowanceDependents = this.validateDateRanges(minAllowanceStart, maxAllowanceEnd, ['amap', 'eba', 'dsd_off_invoice'], 'allowance')
          }
          if (!validAdDependents || !validAllowanceDependents) return
        }
        const validFields = validDates && this.$refs.form.validate()
        if (!validFields) {
          return this.emitAlert(true, 'warning', 'Please check your form for errors')
        }
        this.checkLateSubmissions()
        this.dialog = true
    },
    closeUploadDialog() {
      this.dialog = false
    },
    closeConfirmDatesDialog() {
      this.confirmDatesDialog = false
    },
    confirmUpload() {
      this.closeUploadDialog()
      this.upload()
    },
    multiVendorChange(newValue) {
      if (newValue) {
        this.payload.vendor_party_id = null
        this.payload.subvendor_party_id = null
      }
    },
    resetSpecialAdLength() {
      this.payload.special_ad_length = false
    },
    async upload() {
      try {
        await this.processFile(this.file)
        this.emitAlert(true, 'success', 'File(s) have been successfully uploaded and are now being processed.')
        this.$emit('closeUpload', true)
        this.reset()
      } catch (err) {
        this.handleError(err)
      }
    },
    async processFile(file) {
      const blob = new Blob([file.upload_file], { type: `${file.type}` })
      const formData = new FormData()
      const pathEnv = `${process.env.VUE_APP_FILE_PATH}`
      const today = (new Date()).toISOString().split('T')[0]

      formData.append('path', `${pathEnv}/${this.tenant}/uploads/${today}/`)
      formData.append('name', `${file.name}`)
      formData.append('upload_file', blob, `${file.name}`)
      formData.append('product_line', `${this.product_line}`)
      formData.append('is_complex', this.is_complex)

      if (this.tenant === 'alliance-retail-group') {
        const adExcludedPartyIds = []
        const mappedAdGroups = this.payload.ad_group_party_ids.flatMap(ag => {
          if (ag.party_type?.constant !== 'AD_GROUP') return []
          const totalExcluded = this.totalPromoExcludedAdGroups.some(g => g.id === ag.id)
          const adExcluded = this.adPromoOnlyExcludedAdGroups.some(g => g.id === ag.id)
          if (totalExcluded) return []
          if (adExcluded && !this.manuallyAdded.includes(ag)) {
            adExcludedPartyIds.push(ag.id)
          }
          return ag.id
        })

        this.payload.non_allowance = this.adNonAllowanceSelected
        this.payload.ad_group_party_ids = mappedAdGroups
        this.payload.ad_promo_excluded_party_ids = adExcludedPartyIds
        const { lump_sum_allowance, tpr_fee } = this.payload
        const numericAdFee = this.getNumericPrice(lump_sum_allowance)
        const numericTPRFee = this.getNumericPrice(tpr_fee)
        this.payload.lump_sum_allowance = (numericAdFee === 0) ? null : numericAdFee
        this.payload.tpr_fee = (numericTPRFee === 0) ? null : numericTPRFee
      }
      formData.append('payload', JSON.stringify(this.payload))
      if (this.reupload) {
        return ContractUpload.rerunContractUpload(formData, this.uploadId)
      } else {
        return ContractUpload.uploadContract(formData)
      }
    },
    async downloadTemplate(is_complex) {
      try {
        this.emitAlert(true, 'info', "Template downloading", [], true)
        const response = await ContractUpload.downloadTemplate(is_complex)
        if (response?.data?.url) {
          const url = response.data.url
          window.open(url, '_blank')
        }
      } catch (err) {
        console.error(err)
        this.emitAlert(true, 'error', "Error while downloading template")
      }
    },
    async downloadContract () {
      this.downloading = true
      try {
        const { data } =  await ContractUpload.downloadContract({ upload_id: this.selectedContract.upload_id })
        if (data?.url) {
          location.href = data.url
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.downloading = false
      }
    },
    async updateSubvendors() {
      this.loadingSubvendors = true
      try {
        this.subvendors = []
        const subvendorParties = await this.getDownstreamSubvendorParties(this.payload.vendor_party_id)
        if (subvendorParties.length > 0) {
          this.subvendors = subvendorParties.sort((a, b) => a.name.localeCompare(b.name))
        }
      } catch (err) {
        this.handleError(err)
      }
      this.loadingSubvendors = false
    },
    async getDownstreamSubvendorParties (vendor_id) {
      const downstreamSubvendorRes = await PartyRel.downstreamRelationshipSearch({ to_party_ids: [vendor_id] })
      if (downstreamSubvendorRes.data?.length !== 0) {
        let downstreamSubvendors = downstreamSubvendorRes.data
          .filter(rel => rel.from_rel_type === 'CHILD_VENDOR_FOR')
          .map(rel => {
            let subvendor = {}
            subvendor.name = rel.from_party_name
            subvendor.party_name = `${subvendor.name}`
            subvendor.id = rel.from_party_id
            return subvendor
          })
        if (this.limitAccessByRelatedSubvendors) {
          downstreamSubvendors = downstreamSubvendors.filter(s => this.relatedSubvendorPartyIds.includes(s.id))
        }
        return downstreamSubvendors
      } else {
        return []
      }
    },
    sortPromoCategories(categories) {
      return categories.sort((a, b) => {
        // 'day' options before the 'week' options
        const aNormalizedName = a.name.toLowerCase()?.replaceAll('-', ' ')
        const bNormalizedName = b.name.toLowerCase()?.replaceAll('-', ' ')
        return aNormalizedName.includes('day') && bNormalizedName.includes('week') ? -1 : a.name.localeCompare(b.name)
      })
    },
    payloadDatesSelected(value) {
      return (this.payload[value]?.start_dt && this.payload[value]?.end_dt)
    },
    clearNonTPRValues() {
      this.payload = {
        ...this.payload,
        non_allowance: false,
        dsd: false,
        ad_billback_category_id: null,
        pcaf_promo_category_id: null,
        lump_sum_allowance: null,
        special_ad_length: false,
        edlc: { start_dt: null, end_dt:  null },
        dsd_off_invoice: { start_dt: null, end_dt: null },
        ad_1: { start_dt: null, end_dt:  null, offer_start_date: null, offer_end_date: null },
        ad_2: { start_dt: null, end_dt:  null, offer_start_date: null, offer_end_date: null },
        ad_3: { start_dt: null, end_dt:  null, offer_start_date: null, offer_end_date: null },
        digital_coupon: false,
        digital_coupon_note: null
      }
    }
  }
}
</script>